import { Link } from 'react-router-dom';
import { SparklesIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getTierDisplayName, getUpgradeMessage, getRequiredTier } from '../utils/tierPermissions';

/**
 * TierUpgradePrompt Component
 * 
 * Shows an upgrade prompt when user tries to access a feature above their tier.
 * 
 * @param {object} props
 * @param {string} props.feature - The feature being restricted
 * @param {string} props.currentTier - User's current tier
 * @param {boolean} props.show - Whether to show the prompt
 * @param {function} props.onClose - Callback when prompt is dismissed
 * @param {string} props.variant - Display variant: 'banner', 'modal', 'inline' (default: 'banner')
 */
export default function TierUpgradePrompt({ 
  feature, 
  currentTier, 
  show = false, 
  onClose,
  variant = 'banner' 
}) {
  if (!show) return null;

  const requiredTier = getRequiredTier(feature);
  const message = getUpgradeMessage(feature);
  const requiredTierName = getTierDisplayName(requiredTier);
  const currentTierName = getTierDisplayName(currentTier);

  // Banner variant (top of page/section)
  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <SparklesIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Upgrade to Unlock This Feature
            </h3>
            <div className="mt-2 text-sm text-gray-700">
              <p>{message}</p>
              <p className="mt-2">
                Your current tier: <span className="font-semibold">{currentTierName}</span> → 
                Required: <span className="font-semibold">{requiredTierName}</span>
              </p>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Link
                to="/#pricing"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors shadow-sm"
              >
                View Pricing Plans
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Maybe Later
                </button>
              )}
            </div>
          </div>
          {onClose && (
            <div className="ml-auto flex-shrink-0">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Modal variant (centered overlay)
  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
            onClick={onClose}
          />
          
          {/* Modal content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
            {onClose && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
            
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
                <SparklesIcon className="h-8 w-8 text-amber-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Upgrade Required
              </h3>
              
              <p className="text-gray-700 mb-2">
                {message}
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 my-6">
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between items-center">
                    <span>Your tier:</span>
                    <span className="font-semibold text-gray-900">{currentTierName}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span>Required:</span>
                    <span className="font-semibold text-amber-600">{requiredTierName}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col gap-3">
                <Link
                  to="/#pricing"
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors shadow-sm"
                >
                  View Pricing Plans
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                {onClose && (
                  <button
                    onClick={onClose}
                    className="w-full px-6 py-3 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Maybe Later
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant (within content)
  if (variant === 'inline') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <SparklesIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 mb-1">
              Upgrade to {requiredTierName}
            </p>
            <p className="text-sm text-gray-700 mb-3">
              {message}
            </p>
            <Link
              to="/#pricing"
              className="inline-flex items-center text-sm font-medium text-amber-700 hover:text-amber-800"
            >
              View Plans
              <ArrowRightIcon className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
